/**
 * Changelog Modal Component
 * Displays the application changelog in a styled, accessible modal dialog.
 * Lazy-loads changelog content when opened.
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Loader2, FileText, Tag, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { coreAPI } from '../lib/api';

interface ChangelogModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface VersionBlock {
  version: string;
  date: string;
  sections: { title: string; items: string[] }[];
}

function parseChangelog(content: string): VersionBlock[] {
  const versions: VersionBlock[] = [];
  let current: VersionBlock | null = null;
  let currentSection: { title: string; items: string[] } | null = null;

  const lines = content.split('\n');
  for (const line of lines) {
    if (line.startsWith('## ')) {
      if (current && current.sections.some(s => s.items.length > 0)) {
        versions.push(current);
      }
      const match = line.match(/^## \[(.+?)\](?:\s*-\s*(.+))?/);
      if (match) {
        current = { version: match[1], date: match[2]?.trim() || '', sections: [] };
        currentSection = null;
      }
    } else if (line.startsWith('### ') && current) {
      currentSection = { title: line.substring(4).trim(), items: [] };
      current.sections.push(currentSection);
    } else if ((line.startsWith('- ') || line.startsWith('* ')) && currentSection) {
      currentSection.items.push(line.substring(2).trim());
    }
  }
  if (current && current.sections.some(s => s.items.length > 0)) {
    versions.push(current);
  }

  return versions;
}

/** Accessible color tokens — all pass WCAG AA against white/light backgrounds */
const SECTION_COLORS: Record<string, string> = {
  Added: 'text-emerald-700 dark:text-emerald-400',
  Changed: 'text-blue-700 dark:text-blue-400',
  Fixed: 'text-amber-700 dark:text-amber-300',
  Removed: 'text-red-700 dark:text-red-400',
  Deprecated: 'text-gray-600 dark:text-gray-400',
  Security: 'text-purple-700 dark:text-purple-400',
};

const SECTION_DOT: Record<string, string> = {
  Added: 'bg-emerald-500',
  Changed: 'bg-blue-500',
  Fixed: 'bg-amber-500',
  Removed: 'bg-red-500',
  Deprecated: 'bg-gray-400',
  Security: 'bg-purple-500',
};

const MAX_VISIBLE_ITEMS = 5;

function SectionBlock({ title, items }: { title: string; items: string[] }) {
  const [expanded, setExpanded] = useState(false);
  const colorClass = SECTION_COLORS[title] || SECTION_COLORS['Changed'];
  const dotClass = SECTION_DOT[title] || SECTION_DOT['Changed'];
  const hasMore = items.length > MAX_VISIBLE_ITEMS;
  const visibleItems = expanded ? items : items.slice(0, MAX_VISIBLE_ITEMS);

  if (items.length === 0) return null;

  return (
    <div className="mb-3" role="group" aria-label={`${title} changes`}>
      <div className="flex items-center gap-2 mb-1.5">
        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide ${colorClass}`}>
          <span className={`h-2 w-2 rounded-full ${dotClass} shrink-0`} aria-hidden="true" />
          {title}
        </span>
        <span className="text-xs text-muted-foreground">
          {items.length} {items.length === 1 ? 'change' : 'changes'}
        </span>
      </div>
      <ul className="space-y-1 ml-4" role="list">
        {visibleItems.map((item, ii) => (
          <li key={ii} className="text-sm text-foreground leading-relaxed list-disc">
            {item}
          </li>
        ))}
      </ul>
      {hasMore && (
        <Button
          variant="ghost"
          size="sm"
          className="mt-1 ml-4 h-7 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          aria-label={expanded ? `Show less ${title} changes` : `Show ${items.length - MAX_VISIBLE_ITEMS} more ${title} changes`}
        >
          {expanded ? (
            <><ChevronUp className="h-3 w-3 mr-1" /> Show less</>
          ) : (
            <><ChevronDown className="h-3 w-3 mr-1" /> {items.length - MAX_VISIBLE_ITEMS} more</>
          )}
        </Button>
      )}
    </div>
  );
}

export function ChangelogModal({ open, onOpenChange }: ChangelogModalProps) {
  const [changelog, setChangelog] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && !changelog) {
      loadChangelog();
    }
  }, [open, changelog]);

  const loadChangelog = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await coreAPI.getChangelog();
      setChangelog(response.data.changelog);
    } catch (err: unknown) {
      console.error('Failed to load changelog:', err);
      setError('Failed to load changelog');
    } finally {
      setLoading(false);
    }
  };

  const versions = changelog ? parseChangelog(changelog) : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-hidden flex flex-col" aria-describedby="changelog-description">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <FileText className="h-5 w-5 text-primary" aria-hidden="true" />
            What's New
          </DialogTitle>
          <DialogDescription id="changelog-description">
            Recent updates and improvements to Helix
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2 -mr-2" role="log" aria-label="Version history">
          {loading ? (
            <div className="flex items-center justify-center py-12" role="status">
              <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden="true" />
              <span className="sr-only">Loading changelog</span>
            </div>
          ) : error ? (
            <div className="text-center py-12 text-muted-foreground" role="alert">
              <p>{error}</p>
            </div>
          ) : versions.length > 0 ? (
            <div className="space-y-5">
              {versions.map((v, vi) => (
                <article key={vi} className={vi > 0 ? 'border-t pt-4' : ''} aria-label={`Version ${v.version}`}>
                  {/* Version header */}
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <Badge variant={vi === 0 ? 'default' : 'secondary'} className="text-sm font-mono">
                      <Tag className="h-3 w-3 mr-1" aria-hidden="true" />
                      v{v.version}
                    </Badge>
                    {vi === 0 && (
                      <Badge variant="outline" className="text-xs border-emerald-400 text-emerald-700 dark:text-emerald-400">
                        Latest
                      </Badge>
                    )}
                    {v.date && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1 ml-auto">
                        <Clock className="h-3 w-3" aria-hidden="true" />
                        <time dateTime={v.date}>{v.date}</time>
                      </span>
                    )}
                  </div>

                  {/* Sections */}
                  {v.sections.filter((s) => s.items.length > 0).map((section, si) => (
                    <SectionBlock key={si} title={section.title} items={section.items} />
                  ))}
                </article>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <p>No changelog available</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ChangelogModal;
